import { useRef } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Upload, Loader2, FileText, Info, AlertCircle, ArrowRight } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { buildChecklist, CAP_DOC_CATALOG, CAP_DOC_INFO, CAP_DOC_BOOST, CLINQ_CAN_BUILD, checklistHasBuildableDocs } from "../../lib/capitalDocChecklist";
import { UploadFailedBadge } from "./UploadFailedBadge";
import { CAPITAL_PURPOSE_LABEL } from "../../lib/quizFunderPreview";

export function DocInfoPopover({ id, label, dark = false }) {
  const info = CAP_DOC_INFO[id];
  if (!info) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`cap-doc-info-${id}`}
          aria-label={`What is ${label}?`}
          className={"flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full transition-colors " +
            (dark ? "text-white/40 hover:bg-white/10 hover:text-emerald-300"
                  : "text-neutral-400 hover:bg-neutral-100 hover:text-[#0A8B85]")}>
          <Info className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" align="end" collisionPadding={12}
        className={"w-72 rounded-xl p-4 shadow-2xl " +
          (dark ? "border border-emerald-400/25 bg-[#04302E] text-white"
                : "border border-neutral-200 bg-white text-neutral-800")}
        data-testid={`cap-doc-popover-${id}`}>
        <p className={"text-[10px] font-bold uppercase tracking-widest " + (dark ? "text-emerald-300" : "text-[#0A8B85]")}>What it is</p>
        <p className={"mt-1 text-xs leading-relaxed " + (dark ? "text-white/85" : "text-neutral-700")}>{info.what}</p>
        <p className="mt-3 text-[10px] font-bold uppercase tracking-widest text-[#F59E0B]">Why funders care</p>
        <p className={"mt-1 text-xs leading-relaxed " + (dark ? "text-white/85" : "text-neutral-700")}>{info.why}</p>
      </PopoverContent>
    </Popover>
  );
}

export function BoostChip({ id, dark = false }) {
  const pts = CAP_DOC_BOOST[id];
  if (!pts) return null;
  return (
    <span data-testid={`cap-doc-boost-${id}`}
      className={"inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-bold " +
        (dark ? "bg-emerald-400/15 text-emerald-300" : "bg-emerald-100 text-emerald-800")}>
      up to +{pts} pts
    </span>
  );
}

function Row({ item, files, busy, onUpload, onRemove, onDiscard }) {
  const inputRef = useRef(null);
  const retryDocRef = useRef(null);
  const okFiles = files.filter((d) => d.upload_status !== "failed");
  const uploaded = okFiles.length > 0;
  const hasFiles = files.length > 0;
  return (
    <div data-testid={`cap-doc-row-${item.id}`}
      className={"rounded-xl border px-4 py-3 " +
        (uploaded ? "border-emerald-200 bg-emerald-50/60" : "border-neutral-200 bg-white")}>
      <div className="flex items-center gap-3">
        {uploaded
          ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
          : <FileText className="h-4 w-4 shrink-0 text-neutral-400" />}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-neutral-800">{item.label}</p>
          {uploaded && (
            <p className="text-[11px] text-emerald-700">
              {okFiles.length} file{okFiles.length === 1 ? "" : "s"} uploaded ✓
            </p>
          )}
        </div>
        {!uploaded && <BoostChip id={item.id} />}
        <DocInfoPopover id={item.id} label={item.label} />
        <button type="button" disabled={busy}
          data-testid={`cap-doc-upload-${item.id}`}
          onClick={() => inputRef.current?.click()}
          className={"inline-flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold disabled:opacity-50 " +
            (uploaded
              ? "border border-neutral-300 text-neutral-600 hover:border-neutral-400"
              : "bg-[#0A8B85] text-white hover:bg-[#066667]")}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          {uploaded ? "Add another" : "Upload"}
        </button>
        <input ref={inputRef} type="file" multiple className="hidden"
          data-testid={`cap-doc-input-${item.id}`}
          onChange={async (e) => {
            const picked = Array.from(e.target.files || []);
            e.target.value = "";
            const retryId = retryDocRef.current;
            retryDocRef.current = null;
            for (const f of picked) await onUpload(f, item.id);
            if (retryId && picked.length && onDiscard) await onDiscard(retryId);
          }} />
      </div>
      {hasFiles && (
        <ul className={"mt-2 space-y-1 border-t pt-2 " + (uploaded ? "border-emerald-100" : "border-neutral-100")} data-testid={`cap-doc-files-${item.id}`}>
          {files.map((d) => (
            <li key={d.doc_id} className="flex items-center justify-between gap-2 text-[11px]">
              <span className="flex min-w-0 items-center gap-1.5 truncate text-neutral-700">
                <FileText className="h-3 w-3 shrink-0 text-[#0A8B85]" />
                <span className="truncate">{d.filename}</span>
                <UploadFailedBadge doc={d}
                  onRetry={() => { retryDocRef.current = d.doc_id; inputRef.current?.click(); }} />
              </span>
              {onRemove && (
                <button type="button" data-testid={`cap-doc-remove-${d.doc_id}`}
                  onClick={() => onRemove(d.doc_id)}
                  className="shrink-0 font-bold text-red-500 hover:text-red-700 hover:underline">
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DocList({ items, docs, uploading, onUpload, onRemove, onDiscard }) {
  return (
    <div className="space-y-2">
      {items.map((item) => (
        <Row key={item.id} item={item}
          files={(docs || []).filter((d) => d.doc_type === item.id)}
          busy={!!uploading?.[item.id]}
          onUpload={onUpload} onRemove={onRemove} onDiscard={onDiscard} />
      ))}
    </div>
  );
}

/** Two-part upload list driven by the founder's capital purposes (quiz
 *  Step 4) and the documents they said they have (first-visit checklist):
 *  1. "Upload what you have" — the docs they told us they have.
 *  2. "Do you have these additional documents?" — everything else for their
 *     capital need; optional, proceed with what you have. */
export const CapitalDocChecklist = ({ purposes, docs, haveIds, uploading, onUpload, onRemove, onDiscard }) => {
  const checklist = buildChecklist(purposes);
  const have = haveIds || new Set();
  const inList = new Set(checklist.map((i) => i.id));
  const extraHave = [...have].filter((id) => !inList.has(id) && CAP_DOC_CATALOG[id])
    .map((id) => ({ id, label: CAP_DOC_CATALOG[id], requirement: "optional" }));
  const yours = [...checklist.filter((i) => have.has(i.id)), ...extraHave];
  const rest = checklist.filter((i) => !have.has(i.id));
  if (yours.length === 0 && rest.length === 0) return null;
  const primary = yours.length ? yours : rest;
  const additional = yours.length ? rest : [];
  const okTypes = new Set((docs || []).filter((d) => d.upload_status !== "failed").map((d) => d.doc_type));
  const primaryDone = primary.filter((i) => okTypes.has(i.id)).length;
  const additionalDone = additional.filter((i) => okTypes.has(i.id)).length;
  const labels = (purposes || []).map((p) => CAPITAL_PURPOSE_LABEL[p]).filter(Boolean);
  const listProps = { docs, uploading, onUpload, onRemove, onDiscard };

  return (
    <div className="mt-6 space-y-4" data-testid="cap-doc-checklist">
      <div className="rounded-2xl border border-[#0A8B85]/30 bg-[#0A8B85]/5 p-4" data-testid="cap-doc-yours">
        <p className="text-sm font-bold text-[#0A8B85]">
          Upload what you have
          <span className="ml-2 text-xs font-normal text-[#0A8B85]/80">({primaryDone}/{primary.length} uploaded)</span>
        </p>
        <p className="mt-0.5 text-xs text-neutral-600">
          {yours.length
            ? "The documents you told us you already have — upload them here."
            : "Upload whatever you have today — we score you on what you provide."}
          {labels.length > 0 && <> Tailored to: {labels.join(", ")}.</>}
        </p>
        <div className="mt-4"><DocList items={primary} {...listProps} /></div>
      </div>

      {additional.length > 0 && (
        <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4" data-testid="cap-doc-additional">
          <p className="text-sm font-bold text-neutral-800">
            Do you have these additional documents?
            <span className="ml-2 text-xs font-normal text-neutral-500">({additionalDone}/{additional.length} uploaded)</span>
          </p>
          <p className="mt-0.5 text-xs text-neutral-600">
            If not, that&apos;s OK — proceed with what you have. Each one you add strengthens your case and lifts your score.
          </p>
          {checklistHasBuildableDocs(additional) && (
            <div className="mt-4 rounded-xl border border-[#0A8B85]/30 bg-white p-3" data-testid="cap-doc-build-help">
              <p className="text-xs font-bold text-[#0A8B85]">
                Don&apos;t have a pitch deck or financial model yet? Clinq can help you build these.
              </p>
              <p className="mt-0.5 text-[11px] text-neutral-600">
                Chat with us on WhatsApp or email capital@clinq.africa — our team helps
                founders create investor-ready versions.
              </p>
            </div>
          )}
          <div className="mt-4"><DocList items={additional} {...listProps} /></div>
        </div>
      )}
    </div>
  );
};

/** Report-page panel: which Required documents (per capital purposes) are
 *  still missing — with a direct path to upload them. */
export const MissingCapitalDocsPanel = ({ purposes, uploadedDocTypes }) => {
  const checklist = buildChecklist(purposes);
  if (checklist.length === 0) return null;
  const have = uploadedDocTypes || new Set();
  const missingReq = checklist.filter((i) => i.requirement === "required" && !have.has(i.id));
  const potential = missingReq.reduce((sum, i) => sum + (CAP_DOC_BOOST[i.id] || 0), 0);
  const labels = (purposes || []).map((p) => CAPITAL_PURPOSE_LABEL[p]).filter(Boolean);
  if (missingReq.length === 0) {
    return (
      <div data-testid="report-capdocs-complete"
        className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
        <p className="inline-flex items-center gap-2 text-sm font-bold text-emerald-800">
          <CheckCircle2 className="h-4 w-4" /> All required documents for your capital needs are uploaded
        </p>
        <p className="mt-1 text-xs text-emerald-700">
          Covering: {labels.join(", ")}. Funders reviewing your profile will find everything they expect.
        </p>
      </div>
    );
  }
  return (
    <div data-testid="report-capdocs-missing"
      className="rounded-2xl border border-amber-300 bg-amber-50 p-5">
      <p className="inline-flex items-center gap-2 text-sm font-bold text-amber-900">
        <AlertCircle className="h-4 w-4" />
        {missingReq.length} required document{missingReq.length > 1 ? "s" : ""} still missing for your capital needs
      </p>
      <p className="mt-1 text-xs text-amber-800">
        Based on your selected needs ({labels.join(", ")}), funders will ask for these before advancing
        {potential > 0 && (
          <> — together they could lift your score by <b data-testid="report-capdocs-potential">up to +{potential} points</b> on your next re-run</>
        )}:
      </p>
      <ul className="mt-3 space-y-1.5">
        {missingReq.map((i) => (
          <li key={i.id} data-testid={`report-capdoc-missing-${i.id}`}
            className="flex items-center gap-2 text-sm text-amber-900">
            <FileText className="h-3.5 w-3.5 shrink-0 text-amber-600" />
            <span className="flex-1">{i.label}</span>
            <BoostChip id={i.id} />
            <DocInfoPopover id={i.id} label={i.label} />
          </li>
        ))}
      </ul>
      {missingReq.some((i) => CLINQ_CAN_BUILD.has(i.id)) && (
        <p className="mt-3 text-xs font-semibold text-amber-900" data-testid="report-capdocs-build-help">
          Don&apos;t have a pitch deck or financial model yet? Clinq can help you build these —
          ask David or email capital@clinq.africa.
        </p>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Link to="/onboarding" data-testid="report-capdocs-upload-link"
          className="inline-flex items-center gap-1.5 rounded-full bg-[#0A8B85] px-4 py-2 text-xs font-bold text-white hover:bg-[#066667]">
          Upload them now <ArrowRight className="h-3.5 w-3.5" />
        </Link>
        <p className="text-[11px] text-amber-700/80">
          Point gains are estimates — your exact score is recalculated when you re-run the analysis.
        </p>
      </div>
    </div>
  );
};
